import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import {
  EfficiencyOverviewResponse,
  useAsyncAction,
  useStoreInformation,
  useTransaction,
} from '../../../../../hooks';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Icons } from '../../../../../stories/atoms';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { formatAmountWithSeparator, isFailureResponse } from '../../../../../utils';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { AreaWidgetType } from '../../../../areaOverview/util';
import { PerformanceDrawerContent } from '../../../../areaOverview/overviewWidget/performanceDrawerContent/PerformanceDrawerContent';

type EfficiencyOverviewWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const EfficiencyOverviewWidget: FC<EfficiencyOverviewWidgetProps> = observer(
  ({ currentDate, interval }) => {
    const { t } = useTranslation();
    const { getFormattedCurrency } = useCurrencyTools(interval, currentDate);
    const { fetchEfficiencyOverviewData } = useTransaction();
    const { currencyCode: targetCurrency } = useStoreInformation();

    const {
      result: efficiencyOverviewData,
      isLoading: efficiencyOverviewLoading,
      isError: efficiencyOverviewError,
      triggerAction: triggerFetchEfficiencyOverview,
    } = useAsyncAction<EfficiencyOverviewResponse>();

    const fetchData = async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });
      const response = await fetchEfficiencyOverviewData(request, targetCurrency);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    };

    const fetchWidgetData = () => {
      triggerFetchEfficiencyOverview(fetchData);
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate]);

    const sliderContent = efficiencyOverviewData ? (
      <PerformanceDrawerContent
        title={t(
          areaOverviewTranslations.performance.drawerTitle.key,
          areaOverviewTranslations.performance.drawerTitle.defaultValue,
        )}
        items={[
          {
            label: t(
              areaOverviewTranslations.efficiencyOverview.headerText.key,
              areaOverviewTranslations.efficiencyOverview.headerText.defaultValue,
            ),
            currentValue: efficiencyOverviewData.effectiveHours,
            currentValueFormatted: formatAmountWithSeparator(efficiencyOverviewData.effectiveHours),
            lastWeek: efficiencyOverviewData.effectiveHoursLW,
            lastWeekFormatted: formatAmountWithSeparator(efficiencyOverviewData.effectiveHoursLW),
            lastYear: efficiencyOverviewData.effectiveHoursLY,
            lastYearFormatted: formatAmountWithSeparator(efficiencyOverviewData.effectiveHoursLY),
            isEffectiveHours: true,
          },
          {
            label: t(
              areaOverviewTranslations.efficiencyOverview.takt1Text.key,
              areaOverviewTranslations.efficiencyOverview.takt1Text.defaultValue,
            ),
            currentValue: efficiencyOverviewData.takt1,
            currentValueFormatted: getFormattedCurrency(efficiencyOverviewData.takt1, { showCurrencyCode: false }),
            lastWeek: efficiencyOverviewData.takt1LW,
            lastWeekFormatted: getFormattedCurrency(efficiencyOverviewData.takt1LW, { showCurrencyCode: false }),
            lastYear: efficiencyOverviewData.takt1LY,
            lastYearFormatted: getFormattedCurrency(efficiencyOverviewData.takt1LY, { showCurrencyCode: false }),
            isEffectiveHours: false,
          },
          {
            label: t(
              areaOverviewTranslations.efficiencyOverview.sptText.key,
              areaOverviewTranslations.efficiencyOverview.sptText.defaultValue,
            ),
            currentValue: efficiencyOverviewData.spt,
            currentValueFormatted: formatAmountWithSeparator(efficiencyOverviewData.spt),
            lastWeek: efficiencyOverviewData.sptLW,
            lastWeekFormatted: formatAmountWithSeparator(efficiencyOverviewData.sptLW),
            lastYear: efficiencyOverviewData.sptLY,
            lastYearFormatted: formatAmountWithSeparator(efficiencyOverviewData.sptLY),
            isEffectiveHours: false,
          },
        ]}
      />
    ) : undefined;

    return (
      <OverviewWidget
        id={AreaWidgetType.EfficiencyOverview}
        isLoading={efficiencyOverviewLoading}
        isError={efficiencyOverviewError}
        errorRetryHandler={() => {
          fetchWidgetData();
        }}
        title={t(
          areaOverviewTranslations.efficiencyOverview.title.key,
          areaOverviewTranslations.efficiencyOverview.title.defaultValue,
        )}
        headerIcon={Icons.CLOCK}
        headerText={t(
          areaOverviewTranslations.efficiencyOverview.headerText.key,
          areaOverviewTranslations.efficiencyOverview.headerText.defaultValue,
        )}
        headerValue={formatAmountWithSeparator(efficiencyOverviewData?.effectiveHours ?? 0)}
        headerComparison={
          efficiencyOverviewData
            ? {
                current: efficiencyOverviewData.effectiveHours,
                lastWeek: efficiencyOverviewData.effectiveHoursLW,
                lastWeekFormatted: formatAmountWithSeparator(efficiencyOverviewData.effectiveHoursLW),
                lastYear: efficiencyOverviewData.effectiveHoursLY,
                lastYearFormatted: formatAmountWithSeparator(efficiencyOverviewData.effectiveHoursLY),
                isEffectiveHours: true,
              }
            : undefined
        }
        widgetText={t(
          areaOverviewTranslations.efficiencyOverview.widgetText.key,
          areaOverviewTranslations.efficiencyOverview.widgetText.defaultValue,
        )}
        statistics={[
          {
            label: t(
              areaOverviewTranslations.efficiencyOverview.takt1Text.key,
              areaOverviewTranslations.efficiencyOverview.takt1Text.defaultValue,
            ),
            value: getFormattedCurrency(efficiencyOverviewData?.takt1 ?? 0, { showCurrencyCode: false }),
            comparison: efficiencyOverviewData
              ? {
                  current: efficiencyOverviewData.takt1,
                  lastWeek: efficiencyOverviewData.takt1LW,
                  lastWeekFormatted: getFormattedCurrency(efficiencyOverviewData.takt1LW, {
                    showCurrencyCode: false,
                  }),
                  lastYear: efficiencyOverviewData.takt1LY,
                  lastYearFormatted: getFormattedCurrency(efficiencyOverviewData.takt1LY, {
                    showCurrencyCode: false,
                  }),
                  isEffectiveHours: false,
                }
              : undefined,
          },
          {
            label: t(
              areaOverviewTranslations.efficiencyOverview.sptText.key,
              areaOverviewTranslations.efficiencyOverview.sptText.defaultValue,
            ),
            value: formatAmountWithSeparator(efficiencyOverviewData?.spt ?? 0),
            comparison: efficiencyOverviewData
              ? {
                  current: efficiencyOverviewData.spt,
                  lastWeek: efficiencyOverviewData.sptLW,
                  lastWeekFormatted: formatAmountWithSeparator(efficiencyOverviewData.sptLW),
                  lastYear: efficiencyOverviewData.sptLY,
                  lastYearFormatted: formatAmountWithSeparator(efficiencyOverviewData.sptLY),
                  isEffectiveHours: false,
                }
              : undefined,
          },
        ]}
        sliderContent={sliderContent}
      />
    );
  },
);
